import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import {
  Fish,
  TrendingUp,
  TrendingDown,
  Bell,
  BellOff,
  ExternalLink,
  Copy,
  Eye,
  AlertTriangle,
  RefreshCw,
  Filter
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { useWallet } from "@/contexts/WalletContext";

interface WhaleTrackerProps {
  coinId?: string;
}

export function WhaleTracker({ coinId }: WhaleTrackerProps) {
   const { publicKey } = useWallet();
   const [watchedWhales, setWatchedWhales] = useState<string[]>([]);
   const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
   const [whaleSizeFilter, setWhaleSizeFilter] = useState([1]); // Min SOL amount
   const [timeRangeFilter, setTimeRangeFilter] = useState("24"); // Hours

  const { data: whaleTrades, isLoading, error: whaleTradesError, refetch: refetchWhaleTrades } = useQuery({
    queryKey: ["whale-trades", coinId, whaleSizeFilter[0], timeRangeFilter],
    queryFn: async () => {
      const timeRangeHours = parseInt(timeRangeFilter);
      const timeAgo = Math.floor(Date.now() / 1000) - timeRangeHours * 3600;

      let query = supabase
        .from("trades")
        .select("*, coins(name, ticker, image)")
        .gte("amount_sol", whaleSizeFilter[0])
        .gte("timestamp", timeAgo)
        .order("timestamp", { ascending: false })
        .limit(50);

      if (coinId) {
        query = query.eq("coin_id", coinId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    refetchInterval: 5000,
    onSuccess: () => {
      setLastUpdate(new Date());
    },
  });

  const toggleWatch = (wallet: string) => {
    if (watchedWhales.includes(wallet)) {
      setWatchedWhales(prev => prev.filter(w => w !== wallet));
      toast({ title: "Removed from watchlist" });
    } else {
      setWatchedWhales(prev => [...prev, wallet]);
      toast({ title: "Added to watchlist", description: "You'll be notified of their trades" });
    }
  };

  const copyWallet = (wallet: string) => {
    navigator.clipboard.writeText(wallet);
    toast({ title: "Wallet address copied" });
  };

  if (whaleTradesError) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Fish className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Whale Activity</h3>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[200px]">
          <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
          <h4 className="text-lg font-semibold mb-2">Failed to Load Whale Trades</h4>
          <p className="text-muted-foreground mb-4 text-center max-w-md">
            {whaleTradesError instanceof Error ? whaleTradesError.message : 'An unexpected error occurred'}
          </p>
          <Button onClick={() => refetchWhaleTrades()} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Fish className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Whale Activity</h3>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-3">
              <div className="flex items-start gap-3">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-5 w-12" />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className="h-3 w-32 mt-1" />
                </div>
                <div className="flex items-center gap-1">
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Fish className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Whale Activity</h3>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <Badge variant="outline" className="text-xs">
              Updated {formatDistanceToNow(lastUpdate, { addSuffix: true })}
            </Badge>
          )}
          <Badge variant="secondary">Live</Badge>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-border bg-secondary/20">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground mb-2 block">
              Min Whale Size: {whaleSizeFilter[0]} SOL
            </label>
            <Slider
              value={whaleSizeFilter}
              onValueChange={setWhaleSizeFilter}
              min={0.1}
              max={100}
              step={0.1}
              className="w-full"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-2 block">
              Time Range
            </label>
            <Select value={timeRangeFilter} onValueChange={setTimeRangeFilter}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last Hour</SelectItem>
                <SelectItem value="6">Last 6 Hours</SelectItem>
                <SelectItem value="24">Last 24 Hours</SelectItem>
                <SelectItem value="72">Last 3 Days</SelectItem>
                <SelectItem value="168">Last Week</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
        {whaleTrades && whaleTrades.length > 0 ? (
          whaleTrades.map((trade) => {
            const coin = trade.coins as any;
            const isBuy = trade.type === "buy";
            const isWatched = watchedWhales.includes(trade.wallet || "");
            const timeAgo = trade.timestamp
              ? formatDistanceToNow(new Date(trade.timestamp * 1000), { addSuffix: true })
              : "just now";

            const solAmount = trade.amount_sol || 0;
            let whaleSize = "🐟";
            if (solAmount >= 100) whaleSize = "🐋";
            else if (solAmount >= 50) whaleSize = "🦈";
            else if (solAmount >= 10) whaleSize = "🐬";

            return (
              <div
                key={trade.id}
                className={cn(
                  "p-3 hover:bg-secondary/50 transition-colors",
                  isWatched && "bg-primary/5"
                )}
              >
                <div className="flex items-start gap-3">
                  {/* Whale Icon */}
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center text-xl",
                    isBuy ? "bg-success/20" : "bg-destructive/20"
                  )}>
                    {whaleSize}
                  </div>

                  {/* Trade Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={cn(
                        "text-sm font-semibold",
                        isBuy ? "text-success" : "text-destructive"
                      )}>
                        {isBuy ? "Bought" : "Sold"}
                      </span>
                      <span className="text-sm font-mono font-bold">
                        {solAmount.toFixed(2)} SOL
                      </span>
                      {coin && (
                        <Badge variant="outline" className="text-xs">
                          ${coin.ticker}
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground font-mono">
                        {trade.wallet?.slice(0, 6)}...{trade.wallet?.slice(-4)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {timeAgo}
                      </span>
                    </div>

                    {/* Token amount */}
                    <div className="text-xs text-muted-foreground mt-1">
                      {trade.amount_token?.toLocaleString()} tokens
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => copyWallet(trade.wallet || "")}
                      aria-label="Copy wallet address"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn("h-8 w-8", isWatched && "text-primary")}
                      onClick={() => toggleWatch(trade.wallet || "")}
                      aria-label={isWatched ? "Remove from watchlist" : "Add to watchlist"}
                    >
                      {isWatched ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                    </Button>
                    <a
                      href={`https://solscan.io/account/${trade.wallet}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="View on Solscan">
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <Fish className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No Whale Activity Yet</h3>
            <p className="text-muted-foreground max-w-sm">
              Try adjusting the filters above or check back later.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
